import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Coins, ArrowUpRight, ArrowDownRight, RotateCcw, Plus } from "lucide-react";
import { get } from "@/lib/api";

const PACKAGES = [
  { credits: 20, price: 499, tag: "" },
  { credits: 50, price: 1099, tag: "Popüler" },
  { credits: 100, price: 1899, tag: "En Avantajlı" },
];

const TX_META = {
  spend: { icon: ArrowDownRight, color: "text-rose-600", bg: "bg-rose-50" },
  purchase: { icon: ArrowUpRight, color: "text-emerald-600", bg: "bg-emerald-50" },
  refund: { icon: RotateCcw, color: "text-blue-600", bg: "bg-blue-50" },
};

export default function Credits() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => get("/me") });
  const { data: txs = [] } = useQuery({ queryKey: ["credit-tx"], queryFn: () => get("/credit-transactions") });

  return (
    <div className="mx-auto max-w-4xl px-5 py-10 lg:px-8">
      <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">Kredi Bakiyem</h1>

      {/* Balance card */}
      <div className="mt-6 overflow-hidden rounded-3xl bg-[#2D2926] p-8 text-white">
        <p className="text-sm text-white/60">Mevcut bakiye</p>
        <div className="mt-2 flex items-center gap-3">
          <Coins className="h-9 w-9 text-[#E8DED1]" />
          <span data-testid="credits-total" className="font-display text-6xl font-semibold">{me?.credits ?? "—"}</span>
          <span className="text-white/60">kredi</span>
        </div>
        <p className="mt-3 text-sm text-white/50">Kredilerini istediğin stüdyoda, istediğin seansta kullanabilirsin.</p>
      </div>

      {/* Packages */}
      <h2 className="mt-12 font-display text-2xl font-semibold">Kredi paketleri</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {PACKAGES.map((p) => (
          <div key={p.credits} className={`relative rounded-2xl border bg-white p-6 ${p.tag === "Popüler" ? "border-[#8C6A5D] ring-1 ring-[#8C6A5D]" : "border-[#E5E0D8]"}`}>
            {p.tag && <span className="absolute -top-2.5 left-6 rounded-full bg-[#8C6A5D] px-3 py-0.5 text-xs font-medium text-white">{p.tag}</span>}
            <div className="flex items-baseline gap-1"><span className="font-display text-4xl font-semibold text-[#8C6A5D]">{p.credits}</span><span className="text-[#5C5856]">kredi</span></div>
            <p className="mt-2 text-2xl font-semibold">₺{p.price}</p>
            <button
              data-testid={`buy-package-${p.credits}`}
              onClick={() => toast.info("Demo modu · Ödeme entegrasyonu sonraki aşamada")}
              className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-full bg-[#8C6A5D] py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#705448]"
            >
              <Plus className="h-4 w-4" /> Satın al
            </button>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <h2 className="mt-12 font-display text-2xl font-semibold">Kredi hareketleri</h2>
      <div className="mt-4 divide-y divide-[#F0EAE0] rounded-2xl border border-[#E5E0D8] bg-white">
        {txs.length === 0 && <p className="p-6 text-sm text-[#5C5856]">Henüz işlem yok.</p>}
        {txs.map((t) => {
          const m = TX_META[t.type] || TX_META.spend;
          return (
            <div key={t.id} className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-full ${m.bg}`}><m.icon className={`h-4 w-4 ${m.color}`} /></div>
                <div>
                  <p className="text-sm font-medium">{t.note}</p>
                  <p className="text-xs text-[#9C948B]">{new Date(t.created_at).toLocaleDateString("tr-TR")}</p>
                </div>
              </div>
              <span className={`font-semibold ${t.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
